import { toSquareWebp } from "./avatars.js";
import { DemoError, demoFranchises, demoSquads } from "./demo.js";
import { supabase } from "./supabase.js";
import type { Franchise, Squad } from "./types.js";

const LOGO_BUCKET = "franchise-logos";

/** The code `appoint_captain` raises when the pool's category refuses the member. A form that
 * sees it asks for the override's reason instead of showing the message. See 20261001001000. */
export const CATEGORY_REFUSAL = "AF001";

/** A database refusal, with the code PostgREST gave it. */
export class Refusal extends Error {
  constructor(
    message: string,
    readonly code: string | null,
  ) {
    super(message);
    this.name = "Refusal";
  }
}

interface CaptainRow {
  id: string;
  display_name: string;
  avatar_url: string | null;
}

interface SquadRow {
  id: string;
  franchise_id: string;
  event_id: string;
  captain: CaptainRow | null;
}

interface FranchiseRow {
  id: string;
  tournament_id: string;
  name: string;
  short_name: string | null;
  logo_url: string | null;
  colour: string | null;
  tagline: string | null;
  squads: SquadRow[];
}

const FRANCHISE_COLUMNS = "id, tournament_id, name, short_name, logo_url, colour, tagline";
const SQUAD_COLUMNS = "id, franchise_id, event_id, captain:members (id, display_name, avatar_url)";

export function toFranchise(row: Omit<FranchiseRow, "squads">): Franchise {
  return {
    id: row.id,
    tournamentId: row.tournament_id,
    name: row.name,
    shortName: row.short_name,
    logoUrl: row.logo_url,
    colour: row.colour,
    tagline: row.tagline,
  };
}

/**
 * `purses` is what `squad_purses` returned, which is nothing for anybody but an organiser —
 * so a squad with no entry has a purse of `null`, not of zero. Zero is a number an organiser
 * set, and showing it to a member would say something untrue.
 */
export function toSquad(row: SquadRow, purses: ReadonlyMap<string, number>): Squad {
  return {
    id: row.id,
    franchiseId: row.franchise_id,
    eventId: row.event_id,
    captain: row.captain
      ? {
          id: row.captain.id,
          displayName: row.captain.display_name,
          avatarUrl: row.captain.avatar_url,
        }
      : null,
    purse: purses.get(row.id) ?? null,
  };
}

function refuse(error: { message: string; code?: string | null }): never {
  throw new Refusal(error.message, error.code ?? null);
}

export interface FranchiseSetup {
  franchises: Franchise[];
  squads: Squad[];
}

/**
 * Whether a member captains a squad in any pool of this set-up. Captains and organisers are who
 * the auction rules are shown to; the bid link narrows it further to pools still being auctioned.
 */
export function captainsIn(setup: FranchiseSetup, memberId: string | null | undefined): boolean {
  if (!memberId) return false;
  return setup.squads.some((squad) => squad.captain?.id === memberId);
}

/**
 * Every franchise of a tournament and its squads, in creation order. One round trip for the
 * franchises with their squads and captains, and one for the purses, which answers only to
 * organisers.
 */
export async function fetchFranchises(tournamentId: string): Promise<FranchiseSetup> {
  if (!supabase) {
    return {
      franchises: demoFranchises.filter((f) => f.tournamentId === tournamentId),
      squads: demoSquads,
    };
  }

  const [franchiseResult, purseResult] = await Promise.all([
    supabase
      .from("franchises")
      .select(`${FRANCHISE_COLUMNS}, squads (${SQUAD_COLUMNS})`)
      .eq("tournament_id", tournamentId)
      .order("created_at", { ascending: true }),
    supabase.from("squad_purses").select("squad_id, purse"),
  ]);

  if (franchiseResult.error) refuse(franchiseResult.error);
  if (purseResult.error) refuse(purseResult.error);

  const purses = new Map<string, number>(
    (purseResult.data ?? []).map((p) => [p.squad_id as string, p.purse as number]),
  );
  const rows = franchiseResult.data as unknown as FranchiseRow[];

  return {
    franchises: rows.map(toFranchise),
    squads: rows.flatMap((row) => row.squads.map((s) => toSquad(s, purses))),
  };
}

export interface FranchiseInput {
  name: string;
  shortName: string | null;
  colour: string | null;
  tagline: string | null;
}

function toColumns(input: Partial<FranchiseInput>): Record<string, string | null> {
  const columns: Record<string, string | null> = {};
  if (input.name !== undefined) columns.name = input.name;
  if (input.shortName !== undefined) columns.short_name = input.shortName?.trim() || null;
  if (input.colour !== undefined) columns.colour = input.colour || null;
  if (input.tagline !== undefined) columns.tagline = input.tagline?.trim() || null;
  return columns;
}

export async function createFranchise(
  tournamentId: string,
  input: FranchiseInput,
): Promise<Franchise> {
  if (!supabase) throw new DemoError();

  const { data, error } = await supabase
    .from("franchises")
    .insert({ tournament_id: tournamentId, ...toColumns(input) })
    .select(FRANCHISE_COLUMNS)
    .single();

  if (error) refuse(error);
  return toFranchise(data as unknown as Omit<FranchiseRow, "squads">);
}

/**
 * A change the database lets through matches a row; one it does not — a member, or nobody's
 * franchise — matches none, which is reported as a refusal and not as success.
 */
export async function updateFranchise(id: string, input: Partial<FranchiseInput>): Promise<void> {
  if (!supabase) throw new DemoError();

  const { data, error } = await supabase
    .from("franchises")
    .update(toColumns(input))
    .eq("id", id)
    .select("id");

  if (error) refuse(error);
  if (!data?.length) throw new Refusal("only an organiser can change a franchise", null);
}

export async function deleteFranchise(id: string): Promise<void> {
  if (!supabase) throw new DemoError();

  const { data, error } = await supabase.from("franchises").delete().eq("id", id).select("id");

  if (error) refuse(error);
  if (!data?.length) throw new Refusal("only an organiser can remove a franchise", null);
}

/**
 * Appoint a member captain of a squad, or clear it with a null member. When the pool's
 * category refuses the member the call fails with `CATEGORY_REFUSAL`; pass a reason to
 * override it, which the database records.
 */
export async function appointCaptain(
  squadId: string,
  memberId: string | null,
  overrideReason?: string,
): Promise<void> {
  if (!supabase) throw new DemoError();

  const { error } = await supabase.rpc("appoint_captain", {
    target_squad: squadId,
    target_member: memberId,
    override_reason: overrideReason?.trim() || null,
  });

  if (error) refuse(error);
}

export async function setSquadPurse(squadId: string, purse: number): Promise<void> {
  if (!supabase) throw new DemoError();

  const { data, error } = await supabase
    .from("squads")
    .update({ purse })
    .eq("id", squadId)
    .select("id");

  if (error) refuse(error);
  if (!data?.length) throw new Refusal("only an organiser can set a purse", null);
}

/** The same purse for every squad in a pool. */
export async function setPoolPurses(eventId: string, purse: number): Promise<void> {
  if (!supabase) throw new DemoError();

  const { error } = await supabase.rpc("set_pool_purses", {
    target_event: eventId,
    new_purse: purse,
  });

  if (error) refuse(error);
}

async function setLogoUrl(franchiseId: string, logoUrl: string | null): Promise<void> {
  const { data, error } = await supabase!
    .from("franchises")
    .update({ logo_url: logoUrl })
    .eq("id", franchiseId)
    .select("id");

  if (error) refuse(error);
  if (!data?.length) throw new Refusal("only an organiser can change this", null);
}

/**
 * Set or replace a franchise's logo, contained in a square so a crest is not cut off. The
 * object is overwritten in place, so the URL carries a cache-busting query.
 */
export async function uploadFranchiseLogo(franchiseId: string, file: File): Promise<string> {
  if (!supabase) throw new DemoError();

  if (!file.type.startsWith("image/")) {
    throw new Error("That is not an image.");
  }

  const blob = await toSquareWebp(file, "contain");
  const path = `${franchiseId}/logo.webp`;

  const { error: uploadError } = await supabase.storage
    .from(LOGO_BUCKET)
    .upload(path, blob, { contentType: "image/webp", upsert: true });

  if (uploadError) throw new Error(uploadError.message);

  const {
    data: { publicUrl },
  } = supabase.storage.from(LOGO_BUCKET).getPublicUrl(path);

  const url = `${publicUrl}?v=${Date.now()}`;
  await setLogoUrl(franchiseId, url);
  return url;
}

/** Row first, so a refused request leaves the file alone; then the object itself. */
export async function clearFranchiseLogo(franchiseId: string): Promise<void> {
  if (!supabase) throw new DemoError();

  await setLogoUrl(franchiseId, null);

  const { error } = await supabase.storage.from(LOGO_BUCKET).remove([`${franchiseId}/logo.webp`]);
  if (error) throw new Error(error.message);
}
